import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
	AlignLeft,
	ArrowUpRight,
	Building2,
	Clock,
	MapPin,
	Plane,
	User,
	Users,
	Video,
	X
} from 'lucide-react';

import QuickAccessDrawer, {
	QuickAccessNotice
} from '@/features/crm-quick-access/components/QuickAccessDrawer';
import {
	VISIT_LOCATION_LABEL,
	dateKey,
	daysUntil,
	dueLabel,
	eventStart,
	eventTypeMeta,
	meetingDateLabel,
	meetingPlace,
	meetingsSummary,
	organizerLabel,
	startOfDay
} from '@/features/crm-quick-access/utils/format';

// Buyer visits are coloured by location, selection meetings by type (see
// .crm-qa-loc--* in the styles).
const VISIT_LOCATION_KEYS = Object.keys(VISIT_LOCATION_LABEL);
const MEETING_TYPE_LABEL = { preselection_meeting: 'Pre-selection', selection_meeting: 'Selection' };
const MEETING_TYPE_KEYS = Object.keys(MEETING_TYPE_LABEL);

const isSelectionMeeting = (event) => Boolean(MEETING_TYPE_LABEL[event.eventType]);

const meetingClass = (event) => {
	if (isSelectionMeeting(event)) return `crm-qa-loc--${event.eventType}`;
	return `crm-qa-loc--${VISIT_LOCATION_LABEL[event.visitLocation] ? event.visitLocation : 'unknown'}`;
};

/** Online visits get a video icon instead of the travel one. */
const meetingIcon = (event) => {
	if (isSelectionMeeting(event)) return eventTypeMeta(event).icon;
	return event.visitLocation === 'ONLINE' ? Video : Plane;
};

/** "Buyer visit · UK", "Selection meeting". */
const meetingKindLabel = (event) => {
	if (isSelectionMeeting(event)) return eventTypeMeta(event).label;
	return VISIT_LOCATION_LABEL[event.visitLocation]
		? `Buyer visit · ${VISIT_LOCATION_LABEL[event.visitLocation]}`
		: 'Buyer visit';
};

const MeetingCard = ({ event, due, onClick }) => {
	const place = meetingPlace(event);
	const organizer = organizerLabel(event);
	const Icon = meetingIcon(event);
	return (
		<button
			type="button"
			className={`crm-qa-event ${meetingClass(event)}`}
			onClick={onClick}
			title={meetingKindLabel(event)}
		>
			<Icon size={14} className="crm-qa-event__icon crm-qa-loc__icon" />
			<span className="crm-qa-event__text">
				<span className="crm-qa-event__title">{event.title}</span>
				<span className="crm-qa-event__detail">
					<span className="crm-qa-event__date">{meetingDateLabel(event)}</span>
					<span className="crm-qa-event__separator" aria-hidden="true">
						·
					</span>
					<span className="crm-qa-event__due">{due}</span>
				</span>
				{(event.brandName || place) && (
					<span className="crm-qa-event__meta">
						{event.brandName && (
							<span
								className="crm-qa-event__meta-item crm-qa-event__meta-item--brand"
								title="Brand"
							>
								<Building2 size={12} />
								<span>{event.brandName}</span>
							</span>
						)}
						{place && (
							<span className="crm-qa-event__meta-item" title="Location">
								<MapPin size={12} />
								<span>{place}</span>
							</span>
						)}
					</span>
				)}
				{organizer && (
					<span className="crm-qa-event__meta" title={event.organizer?.email ?? undefined}>
						<span className="crm-qa-event__meta-item">
							<User size={12} />
							<span>{organizer}</span>
						</span>
					</span>
				)}
			</span>
		</button>
	);
};

const DetailRow = ({ icon: Icon, label, children }) => (
	<div className="crm-qa-detail">
		<Icon size={14} className="crm-qa-detail__icon" />
		<div className="crm-qa-detail__text">
			<dt>{label}</dt>
			<dd>{children}</dd>
		</div>
	</div>
);

/** Read-only details of one meeting, stacked above the drawer. Editing stays in the calendar. */
const MeetingDetailsDialog = ({ event, due, onClose, onGoToCalendar }) => {
	const dialogRef = useRef(null);
	const onCloseRef = useRef(onClose);
	useEffect(() => {
		onCloseRef.current = onClose;
	}, [onClose]);

	useEffect(() => {
		// Capture phase, so Escape closes the popup and not the drawer underneath.
		const handleKeyDown = (keyEvent) => {
			if (keyEvent.key !== 'Escape') return;
			keyEvent.stopPropagation();
			onCloseRef.current();
		};
		document.addEventListener('keydown', handleKeyDown, true);
		dialogRef.current?.focus();
		return () => document.removeEventListener('keydown', handleKeyDown, true);
	}, []);

	const place = meetingPlace(event);
	const organizer = organizerLabel(event);
	// The label already falls back to the email when there is no name.
	const organizerEmail =
		event.organizer && !event.organizer.isYou && event.organizer.name ? event.organizer.email : null;
	const description = event.description?.trim();
	const attendees = event.attendees ?? [];
	const Icon = meetingIcon(event);

	return createPortal(
		<>
			<div className="crm-qa-dialog-overlay" onClick={onClose} aria-hidden="true" />
			<div
				ref={dialogRef}
				className="crm-qa-dialog"
				role="dialog"
				aria-modal="true"
				aria-labelledby="crm-qa-dialog-title"
				tabIndex={-1}
			>
				<header className="crm-qa-dialog__header">
					<div className="crm-qa-dialog__heading">
						<p className="crm-qa-drawer__eyebrow">Meeting details</p>
						<h2 id="crm-qa-dialog-title" className="crm-qa-dialog__title">
							{event.title}
						</h2>
						<span className={`crm-qa-dialog__chip ${meetingClass(event)}`}>
							<Icon size={12} className="crm-qa-loc__icon" />
							{meetingKindLabel(event)}
						</span>
					</div>
					<button
						type="button"
						className="crm-qa-icon-button"
						onClick={onClose}
						title="Close"
						aria-label="Close"
					>
						<X size={16} />
					</button>
				</header>

				<dl className="crm-qa-dialog__body">
					<DetailRow icon={Clock} label="When">
						{meetingDateLabel(event)}
						<span className="crm-qa-detail__due">· {due}</span>
					</DetailRow>
					{event.brandName && (
						<DetailRow icon={Building2} label="Brand">
							{event.brandName}
						</DetailRow>
					)}
					{place && (
						<DetailRow icon={MapPin} label="Location">
							{place}
						</DetailRow>
					)}
					{organizer && (
						<DetailRow icon={User} label="Organizer">
							{organizer}
							{organizerEmail && <span className="crm-qa-detail__sub">{organizerEmail}</span>}
						</DetailRow>
					)}
					{attendees.length > 0 && (
						<DetailRow icon={Users} label={`Invitees (${attendees.length})`}>
							<ul className="crm-qa-detail__list">
								{attendees.map((attendee) => (
									<li key={attendee.email}>
										{attendee.name ?? attendee.email}
										{attendee.name && (
											<span className="crm-qa-detail__email">{attendee.email}</span>
										)}
									</li>
								))}
							</ul>
						</DetailRow>
					)}
					{description && (
						<DetailRow icon={AlignLeft} label="Description">
							<span className="crm-qa-detail__description">{description}</span>
						</DetailRow>
					)}
				</dl>

				<footer className="crm-qa-dialog__footer">
					<button type="button" className="crm-qa-secondary-button" onClick={onClose}>
						Close
					</button>
					<button type="button" className="crm-qa-primary-button" onClick={onGoToCalendar}>
						Go to calendar
						<ArrowUpRight size={14} />
					</button>
				</footer>
			</div>
		</>,
		document.body
	);
};

const CalendarDrawer = ({ open, onClose, dock, feed, openCrm }) => {
	// "Today" follows the last load, which the toolbar refreshes on every opening.
	const today = startOfDay(new Date(feed.loadedAt));
	const loaded = feed.loadedAt !== 0;

	const openFullCalendar = (key) => openCrm(`/my-projects?tab=calendar${key ? `&date=${key}` : ''}`);

	// Kept as an id so the popup follows refreshes, and closes if the meeting drops out of the feed.
	const [detailsId, setDetailsId] = useState(null);
	const detailsEvent =
		open && detailsId ? (feed.events.find((event) => event.id === detailsId) ?? null) : null;

	const renderBody = () => {
		if (!loaded && !feed.error) {
			return (
				<div className="crm-qa-skeleton crm-qa-skeleton--meetings">
					{Array.from({ length: 5 }).map((_, index) => (
						<div key={index} className="crm-qa-skeleton__row" />
					))}
				</div>
			);
		}
		if (feed.error && !loaded) {
			return (
				<QuickAccessNotice
					title="Meetings unavailable"
					body={feed.error}
					actionLabel="Try again"
					onAction={() => feed.refresh()}
				/>
			);
		}
		if (feed.events.length === 0) {
			return (
				<QuickAccessNotice
					title="No upcoming meetings"
					body="Buyer visits and selection meetings scheduled in the next two months will show up here."
					actionLabel="Open Calendar"
					onAction={() => openFullCalendar()}
				/>
			);
		}
		return (
			<ul className="crm-qa-calendar-list">
				{feed.events.map((event) => (
					<li key={event.id}>
						<MeetingCard
							event={event}
							due={dueLabel(daysUntil(event, today))}
							onClick={() => setDetailsId(event.id)}
						/>
					</li>
				))}
			</ul>
		);
	};

	const legend = [
		...VISIT_LOCATION_KEYS.filter((key) =>
			feed.events.some((event) => event.eventType === 'buyer_visit' && event.visitLocation === key)
		).map((key) => ({ key, label: VISIT_LOCATION_LABEL[key] })),
		...MEETING_TYPE_KEYS.filter((key) => feed.events.some((event) => event.eventType === key)).map(
			(key) => ({ key, label: MEETING_TYPE_LABEL[key] })
		)
	];

	return (
		<>
			<QuickAccessDrawer
				open={open}
				onClose={() => {
					setDetailsId(null);
					onClose();
				}}
				dock={dock}
				eyebrow="Meetings"
				title={loaded ? meetingsSummary(feed.events, today) : 'Upcoming meetings'}
				refreshing={feed.loading}
				onRefresh={() => feed.refresh()}
				toolbar={
					legend.length > 0 ? (
						<ul className="crm-qa-legend" aria-label="Meeting colours">
							{legend.map((item) => (
								<li key={item.key}>
									<span className={`crm-qa-legend__dot crm-qa-legend__dot--${item.key}`} />
									{item.label}
								</li>
							))}
						</ul>
					) : undefined
				}
				fullViewLabel="Open Calendar"
				onOpenFullView={() => openFullCalendar()}
			>
				{renderBody()}
			</QuickAccessDrawer>
			{detailsEvent && (
				<MeetingDetailsDialog
					event={detailsEvent}
					due={dueLabel(daysUntil(detailsEvent, today))}
					onClose={() => setDetailsId(null)}
					onGoToCalendar={() => {
						setDetailsId(null);
						// An ongoing visit opens on today rather than on the day it began.
						openFullCalendar(
							dateKey(daysUntil(detailsEvent, today) < 0 ? today : eventStart(detailsEvent))
						);
					}}
				/>
			)}
		</>
	);
};

export default CalendarDrawer;
